import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  User,
  signInWithRedirect,
  getRedirectResult,
  OAuthProvider,
  UserCredential,
} from 'firebase/auth';
import { AuthenticationHandler, SelectedTenantInfo, CIAPError } from 'gcip-iap';
import { config } from './config';

/**
 * Custom Authentication Handler for GCIP IAP integration.
 *
 * This handler:
 * 1. Initiates OIDC sign-in with Auth0 via GCIP
 * 2. Handles errors (including permission-denied for no-roles users)
 * 3. Forces token refresh after sign-in to pick up fresh claims
 */
export class BomboraAuthHandler implements AuthenticationHandler {
  private app: FirebaseApp | null = null;
  private authInstances: Map<string, Auth> = new Map();
  private lastAuth: Auth | null = null;

  // Optional: Set language code for localized UI
  languageCode?: string | null = null;

  /**
   * Returns a Firebase Auth instance for the given API key and tenant.
   * The gcip-iap library calls this to get the Auth instance for sign-in.
   */
  getAuth(apiKey: string, tenantId: string | null): Auth {
    console.log('getAuth called', { apiKey: apiKey.substring(0, 10) + '...', tenantId });

    const key = `${apiKey}-${tenantId || 'default'}`;

    if (this.authInstances.has(key)) {
      const auth = this.authInstances.get(key)!;
      console.log('Returning cached auth instance', { tenantId: auth.tenantId });
      this.lastAuth = auth;
      return auth;
    }

    // Initialize Firebase app if not already done (reuse existing app if already initialized)
    if (!this.app) {
      this.app = getApps().length === 0
        ? initializeApp({
            apiKey: config.firebase.apiKey || apiKey,
            authDomain: config.firebase.authDomain,
            projectId: config.firebase.projectId,
          })
        : getApp();
      console.log('Initialized Firebase app', { authDomain: config.firebase.authDomain });
    }

    const auth = getAuth(this.app);

    // Set tenant ID if provided
    if (tenantId) {
      auth.tenantId = tenantId;
      console.log('Set tenant ID on auth instance', { tenantId });
    } else {
      console.warn('No tenant ID provided to getAuth');
    }

    this.authInstances.set(key, auth);
    this.lastAuth = auth;

    return auth;
  }

  /**
   * Initiates the sign-in flow with Auth0 OIDC provider.
   * Called by gcip-iap when authentication is needed.
   */
  async startSignIn(
    auth: Auth,
    _match?: SelectedTenantInfo
  ): Promise<UserCredential> {
    console.log('startSignIn called', {
      tenantId: auth.tenantId,
      providerId: config.auth0.oidcProviderId,
    });

    this.lastAuth = auth;

    // Create OIDC provider for Auth0
    const provider = new OAuthProvider(config.auth0.oidcProviderId);

    // Request offline_access for refresh token support
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');
    provider.addScope('offline_access');

    console.log('Initiating signInWithRedirect to Auth0');
    // Initiate redirect-based sign-in
    await signInWithRedirect(auth, provider);

    // This won't be reached as we're redirecting
    // But we need to return something for TypeScript
    // The actual result comes from getRedirectResult in processUser
    return {} as UserCredential;
  }

  /**
   * Optional: Tenant selection for multi-tenant setups.
   * Not used in single-tenant configuration.
   */
  async selectTenant?(
    _projectConfig: unknown,
    _tenantIds: string[]
  ): Promise<SelectedTenantInfo> {
    // For single tenant, return the configured tenant
    return {
      tenantId: config.tenantId,
      providerIds: [config.auth0.oidcProviderId],
    };
  }

  /**
   * Called after successful sign-in to process the user.
   *
   * This is where we:
   * 1. Get the redirect result with Auth0 credentials
   * 2. Force token refresh to get fresh claims from the blocking function
   */
  async processUser(user: User): Promise<User> {
    if (this.lastAuth) {
      try {
        // Get redirect result to access Auth0 tokens if needed
        const result = await getRedirectResult(this.lastAuth);

        if (result) {
          console.log('Sign-in successful', {
            email: result.user.email,
            providerId: result.providerId,
          });
        }

        // Force token refresh to ensure we have the latest claims
        // The blocking function will have already set fresh claims
        await user.getIdToken(true);

        console.log('Token refreshed with fresh claims');
      } catch (error) {
        console.error('Error processing user:', error);
        // Don't throw here - let the sign-in complete
        // The blocking function is responsible for authorization
      }
    }

    return user;
  }

  /**
   * Handles sign-out completion.
   * Called after the user signs out.
   */
  async completeSignOut(): Promise<void> {
    console.log('Sign-out completed');
    // Clear any cached state
    this.lastAuth = null;
  }

  /**
   * Handles authentication errors.
   *
   * This is where we catch the 'permission-denied' error from the
   * blocking function and redirect to the 403 page.
   */
  handleError(error: Error | CIAPError): void {
    console.error('Authentication error:', error);

    // Check for permission-denied error (no roles)
    if (this.isPermissionDeniedError(error)) {
      console.log('User has no roles, redirecting to 403 page');
      window.location.href = config.urls.noAccessUrl;
      return;
    }

    // Check for other known error types
    if (this.isNetworkError(error)) {
      console.error('Network error during authentication');
      // Could show a retry UI here
    }

    // For other errors, redirect to generic error page
    console.error('Unhandled authentication error, redirecting to error page');
    window.location.href = config.urls.errorUrl;
  }

  /**
   * Optional: Show a progress indicator during long operations.
   */
  showProgressBar?(): void {
    const loader = document.getElementById('loading-indicator');
    if (loader) {
      loader.style.display = 'flex';
    }
  }

  /**
   * Optional: Hide the progress indicator.
   */
  hideProgressBar?(): void {
    const loader = document.getElementById('loading-indicator');
    if (loader) {
      loader.style.display = 'none';
    }
  }

  /**
   * Check if an error is a permission-denied error from the blocking function.
   */
  private isPermissionDeniedError(error: Error | CIAPError): boolean {
    // Log full error details for debugging
    console.log('Checking error type:', {
      code: 'code' in error ? error.code : undefined,
      message: error.message,
      name: 'name' in error ? error.name : undefined,
      fullError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
    });

    const errorCode = 'code' in error ? error.code : '';
    const errorMessage = error.message || '';

    // Check for blocking function error codes from Firebase Auth
    if (errorCode === 'auth/blocking-function-error-response') {
      return true;
    }

    // Check error code
    if (errorCode === 'permission-denied') {
      return true;
    }

    // Check error message patterns
    if (errorMessage.includes('permission-denied') ||
        errorMessage.includes('PERMISSION_DENIED') ||
        errorMessage.includes('blocking-function-error') ||
        errorMessage.includes('do not have access to this application')) {
      return true;
    }

    return false;
  }

  /**
   * Check if an error is a network error.
   */
  private isNetworkError(error: Error | CIAPError): boolean {
    if ('code' in error && error.code === 'network-request-failed') {
      return true;
    }
    return error.message?.includes('network') || false;
  }
}
